<template>
  <div
    :class="{
      'ui-period-picker-input': true,
      'ui-period-picker-input--error': errorMessages.length,
      'ui-period-picker-input--controls-hide-details': hideDetails > 0,
      'ui-period-picker-input--controls-hide-details-compensate': hideDetails === 2,
    }"
  >
    <slot name="label">
      <ui-label
        v-if="label"
        class="mb-4"
        :text="label"
        :help-tooltip="labelHelp"
      ></ui-label>
    </slot>
    <ui-period-picker
      ref="picker"
      :value="value"
      :dateValue="dateValue"
      :text="formatedDate"
      class="m-0"
      :time-in-title="timeInTitle"
      :showTimezone="showTimezone"
      @input="onRangeChanged"
      @menu="adjustPageRange"
    >
      <template v-if="withTimeMenu" #append>
        <div class="period-picker__menu-date-list-wrapper">
          <ui-menu-content :selected="dateValue" :items="items" />
        </div>
      </template>
      <template #actions>
        <ui-button
          class="ml-8"
          icon="fas fa-times"
          :text="t('Close')"
          @click="closeMenu"
        />
      </template>
    </ui-period-picker>

    <ui-input-details :error-messages="errorMessages"></ui-input-details>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { isEqual } from 'lodash';
import { useI18n } from '@/composables';
import { usePeriodPickerRange } from '@/composables/usePeriodPickerRange';

const props = withDefaults(
  defineProps<{
    startTimestamp?: string;
    withTimeMenu?: boolean;
    endTimestamp?: string;
    dateValue?: string;
    value: TimeRange;
    startTimestampErrors?: string[];
    endTimestampErrors?: string[];
    timeInTitle?: string | boolean;
    label?: string;
    labelHelp?: string;
    hideDetails?: number;
    showTimezone?: boolean;
  }>(),
  {
    withTimeMenu: false,
    hideDetails: 0,
    showTimezone: true,
  },
);

const emit = defineEmits<{
  input: [value: TimeRange];
  'update:dateValue': [value: string];
}>();

const { t } = useI18n();
const periodPickerRange = usePeriodPickerRange('YYYY-MM-DD HH:mm:ss');

const picker = ref<{ redrawMenu?: () => void; toggleMenu?: (open: boolean) => void; adjustPageRange?: () => void } | null>(
  null,
);

const firstOpen = ref(true);
const isMenuOpen = ref(false);

const baseItems = periodPickerRange.baseItems;

const errorMessages = computed(() => {
  const startTimestampErrors = props.startTimestampErrors || [];
  const endTimestampErrors = props.endTimestampErrors || [];
  return startTimestampErrors.concat(endTimestampErrors);
});

const formatedDate = computed(() => {
  if (props.dateValue === 'customDateTime' || !props.withTimeMenu) {
    return null;
  }

  const item =
    baseItems.value.find((baseItem) => baseItem.value === props.dateValue) ||
    baseItems.value[0];
  return item.text;
});

const items = computed((): Bemob.ListItem[] =>
  baseItems.value.map((baseItem) => ({
    key: baseItem.value,
    title: baseItem.text,
    click: () => {
      emit('update:dateValue', baseItem.value);
      setDateRange(baseItem.value);
      nextTick(() => {
        adjustDatePickerPageRange();
      });
    },
  })),
);

const redrawMenu = () => {
  picker.value?.redrawMenu?.();
};

const adjustDatePickerPageRange = () => {
  picker.value?.adjustPageRange?.();
};

const adjustPageRange = (menuOpen: boolean) => {
  isMenuOpen.value = menuOpen;

  if (firstOpen.value && menuOpen) {
    picker.value?.adjustPageRange?.();
    firstOpen.value = false;
  }
};

const closeMenu = () => {
  picker.value?.toggleMenu?.(false);
};

const setDateRange = (preset: string) => {
  const rangeCollection = periodPickerRange.getRangeValueCollection({
    timezone: props.value.timezone,
  });

  if (rangeCollection[preset as keyof typeof rangeCollection]) {
    emit('input', {
      ...props.value,
      ...rangeCollection[preset as keyof typeof rangeCollection],
    });
  }
};

const onRangeChanged = (rangeValue: TimeRange) => {
  emit('input', rangeValue);

  const rangeCollection = periodPickerRange.getRangeValueCollection({
    timezone: rangeValue.timezone,
  });
  const keys = Object.keys(rangeCollection);
  let customDate = true;
  const valueTime = { from: rangeValue.from, to: rangeValue.to };

  for (const key of keys) {
    if (
      isEqual(rangeCollection[key as keyof typeof rangeCollection], valueTime)
    ) {
      if (props.dateValue !== key) {
        emit('update:dateValue', key);
      }
      customDate = false;
      break;
    }
  }

  if (customDate) {
    emit('update:dateValue', 'customDateTime');
  }
};

watch(
  () => props.value,
  () => {
    if (!isMenuOpen.value) {
      firstOpen.value = true;
    }
  },
);

defineExpose({
  redrawMenu,
  adjustDatePickerPageRange,
});
</script>

<style lang="scss">
  .ui-period-picker-input {
    display: flex;
    flex-direction: column;

    @import '@/styles/ui/input-general.scss';
    @import '@/styles/ui/input-like-default.scss';

    &--error {
      .ui-period-picker {
        @include inputLikeDefaultBorderError;
      }
    }
  }
  .ui-period-picker__menu{
    .vc-header{
      .vc-title{
        text-transform: capitalize;
      }
    }
  }
</style>
